/**
 * The error a migration raises instead of losing a shift's work.
 *
 * §22 asks for two things and they are easy to conflate: the migration must
 * *refuse to run*, and it must *surface an actionable error*. A thrown exception
 * satisfies the first by itself — Dexie aborts the version-change transaction,
 * IndexedDB rolls the whole upgrade back, and the database is still at the
 * version it was at this morning. It satisfies the second not at all. What
 * reaches the console is `AbortError`, which tells an operator nothing and tells
 * whoever they telephone very little more.
 *
 * So the refusal carries its own evidence: which upgrade, and what it was about
 * to lose, store by store, in the words a person uses for those things. That is
 * what `MigrationBlockedPage` renders and what the exported file is named after.
 */
import type { Shortfall } from "./census"

export class MigrationRefused extends Error {
  readonly version: number
  readonly shortfalls: Shortfall[]

  constructor(version: number, shortfalls: Shortfall[]) {
    super(
      `The upgrade to database version ${version} was stopped because it would have discarded ` +
        `${describe(shortfalls)}. The database has been left as it was.`
    )
    this.name = "MigrationRefused"
    this.version = version
    this.shortfalls = shortfalls
  }

  /** How many rows of unrecoverable work the refusal saved. */
  get atRisk(): number {
    return this.shortfalls.reduce((total, shortfall) => total + (shortfall.before - shortfall.after), 0)
  }
}

/**
 * Dexie wraps whatever an upgrade callback throws, so the refusal arrives at
 * `openOfflineDatabase` as an `UpgradeError` — or a `DexieError` — with the real
 * one hanging off `cause` or `inner`. Recovering it matters: the difference
 * between the blocked page and a generic failure screen is whether the counts
 * survived the trip.
 */
export function refusalWithin(error: unknown, depth = 0): MigrationRefused | null {
  if (error instanceof MigrationRefused) return error
  if (depth > 5 || error === null || typeof error !== "object") return null

  const wrapper = error as { cause?: unknown; inner?: unknown }

  return refusalWithin(wrapper.cause, depth + 1) ?? refusalWithin(wrapper.inner, depth + 1)
}

function describe(shortfalls: Shortfall[]): string {
  if (shortfalls.length === 0) return "work this computer had not sent"

  return shortfalls
    .map(shortfall => `${shortfall.before - shortfall.after} ${shortfall.label}`)
    .join(", ")
}
